/**
 * Copy text to the clipboard, trying the modern API first and the old
 * textarea + `execCommand` way as a real fallback.
 *
 * `navigator.clipboard.writeText` needs a secure context and a genuine user
 * gesture, and several in-app browsers deny it outright — so it is used when
 * it resolves, and the fallback runs both when it is MISSING and when it
 * REJECTS. Falling back only on absence left those users with a copy button
 * that just said it failed.
 *
 * Resolves true when something was written, false when neither way worked.
 */
export async function copyText(text: string): Promise<boolean> {
  return (await tryClipboardApi(text)) || copyViaTextarea(text);
}

async function tryClipboardApi(text: string): Promise<boolean> {
  if (!navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * The pre-Clipboard-API way: select text in an off-screen textarea and let the
 * document copy it. Deprecated, still the only thing that works in a few
 * embedded browsers.
 */
function copyViaTextarea(text: string): boolean {
  try {
    const scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.top = '0';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(scratch);
    return ok;
  } catch {
    return false;
  }
}
